/**
 * MobileScannerPage — Conteo de inventario por escáner de cámara (PWA).
 * Usa BarcodeDetector API nativo del browser (Chrome 83+, Android WebView).
 * Ruta: /inventory/counts/:id/scan
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Camera, CameraOff, Check, X, Package, ChevronLeft, Vibrate, Flashlight } from 'lucide-react'
import apiClient from '@/lib/apiClient'
import { useStockCount, useUpdateStockCount } from '../hooks/useInventory'
import { useTenantLocale, formatNumber } from '@/lib/locale'

interface ScannedItem {
  product_id: string
  product_name: string
  product_sku: string
  expected_qty: string
  counted_qty: string
  barcode: string
}

export default function MobileScannerPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const locale = useTenantLocale()

  const { data: count } = useStockCount(id || '')
  const { mutate: updateCount } = useUpdateStockCount(id || '')

  const [scanning, setScanning] = useState(false)
  const [lastScanned, setLastScanned] = useState<ScannedItem | null>(null)
  const [countedQty, setCountedQty] = useState('')
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([])
  const [error, setError] = useState('')
  const [cameraError, setCameraError] = useState('')

  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const detectorRef = useRef<any>(null)
  const scanIntervalRef = useRef<number | null>(null)

  // ─── Start camera ─────────────────────────────────────────────────────

  const startCamera = useCallback(async () => {
    try {
      setCameraError('')
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      // Check BarcodeDetector support
      if ('BarcodeDetector' in window) {
        detectorRef.current = new (window as any).BarcodeDetector({
          formats: ['code_128', 'ean_13', 'ean_8', 'qr_code', 'code_39'],
        })
      } else {
        setCameraError('Tu navegador no soporta escaneo de códigos de barra. Usa Chrome en Android.')
        return
      }

      setScanning(true)
      startDetection()
    } catch (err: any) {
      setCameraError(err.message || 'No se pudo acceder a la cámara.')
    }
  }, [])

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    setScanning(false)
  }, [])

  const startDetection = useCallback(() => {
    if (scanIntervalRef.current) return
    scanIntervalRef.current = window.setInterval(async () => {
      if (!detectorRef.current || !videoRef.current || !scanning) return
      try {
        const barcodes = await detectorRef.current.detect(videoRef.current)
        if (barcodes.length > 0) {
          const code = barcodes[0].rawValue
          handleBarcodeDetected(code)
        }
      } catch {
        // Un cuadro que no se pudo leer se reintenta en el próximo intervalo.
      }
    }, 300)
  }, [scanning])

  useEffect(() => {
    return () => stopCamera()
  }, [stopCamera])

  // ─── Handle detected barcode ──────────────────────────────────────────

  const handleBarcodeDetected = async (barcode: string) => {
    // Avoid re-scanning same code
    if (lastScanned?.barcode === barcode) return

    // Vibrate
    if (navigator.vibrate) navigator.vibrate(100)

    // Pause scanning
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }

    // Lookup product
    try {
      setError('')
      const res = await apiClient.get('/inventory/products/lookup/', { params: { barcode } })
      const product = res.data.product

      // Find in count lines
      const line = (count?.lines || []).find(
        (l: any) => l.product_id === product.id || l.product_sku === product.sku
      )

      const item: ScannedItem = {
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku,
        expected_qty: line?.expected_qty || '0',
        counted_qty: line?.counted_qty || '0',
        barcode,
      }

      setLastScanned(item)
      setCountedQty(line?.counted_qty || '')
    } catch {
      setError(`Producto no encontrado para código: ${barcode}`)
      // Resume scanning after 2s
      setTimeout(() => { setError(''); startDetection() }, 2000)
    }
  }

  // ─── Confirm counted quantity ─────────────────────────────────────────

  const handleConfirm = () => {
    if (!lastScanned || !count || !id) return

    const qty = countedQty || '0'

    // Update count lines
    const updatedLines = (count.lines || []).map((l: any) => {
      if (l.product_id === lastScanned.product_id) {
        const expected = parseFloat(l.expected_qty || '0')
        const counted = parseFloat(qty)
        return { ...l, counted_qty: qty, difference: String(counted - expected) }
      }
      return l
    })

    updateCount({ lines: updatedLines } as any)

    // Add to scanned list
    setScannedItems(prev => {
      const exists = prev.findIndex(i => i.product_id === lastScanned.product_id)
      const updated = { ...lastScanned, counted_qty: qty }
      if (exists >= 0) {
        const copy = [...prev]
        copy[exists] = updated
        return copy
      }
      return [...prev, updated]
    })

    setLastScanned(null)
    setCountedQty('')

    // Resume scanning
    startDetection()
  }

  const handleSkip = () => {
    setLastScanned(null)
    setCountedQty('')
    startDetection()
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800">
        <button onClick={() => { stopCamera(); navigate(-1) }}
          className="flex items-center gap-1 text-sm text-content-subtle hover:text-white">
          <ChevronLeft size={18} /> Volver
        </button>
        <h1 className="text-sm font-bold">Escaneo de Inventario</h1>
        <span className="text-xs text-content-subtle">
          {scannedItems.length} escaneados
        </span>
      </div>

      {/* Camera viewport */}
      <div className="flex-1 relative flex items-center justify-center bg-black">
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          playsInline
          muted
        />

        {/* Scan overlay */}
        {scanning && !lastScanned && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-64 h-40 border-2 border-green-400 rounded-xl opacity-60">
              <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400 rounded-tl-xl" />
              <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400 rounded-tr-xl" />
              <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400 rounded-bl-xl" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400 rounded-br-xl" />
            </div>
            <p className="absolute bottom-8 text-xs text-green-300 animate-pulse">
              Apunta al código de barras
            </p>
          </div>
        )}

        {/* Not started */}
        {!scanning && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <Camera size={48} className="text-content-muted" />
            <button onClick={startCamera}
              className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold text-sm hover:bg-green-700">
              Activar Cámara
            </button>
          </div>
        )}

        {/* Camera error */}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center">
            <CameraOff size={48} className="text-danger-solid" />
            <p className="text-sm text-red-300">{cameraError}</p>
            <button onClick={startCamera}
              className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm hover:bg-slate-600">
              Reintentar
            </button>
          </div>
        )}

        {/* Barcode error */}
        {error && (
          <div className="absolute bottom-4 left-4 right-4 bg-danger-fill text-white px-4 py-3 rounded-xl text-sm font-medium text-center">
            {error}
          </div>
        )}
      </div>

      {/* Product found panel */}
      {lastScanned && (
        <div className="bg-slate-800 px-4 py-4 space-y-3 border-t border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Package size={20} className="text-green-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-white">{lastScanned.product_name}</p>
              <p className="text-xs text-content-subtle">{lastScanned.product_sku} — Esperado: {lastScanned.expected_qty}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label htmlFor="mobilescannerpage-cantidad-contada" className="text-xs text-content-subtle mb-1 block">Cantidad contada</label>
              <input id="mobilescannerpage-cantidad-contada"
                type="number"
                value={countedQty}
                onChange={e => setCountedQty(e.target.value)}
                autoFocus
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-lg font-bold text-white text-center focus:outline-none focus:ring-2 focus:ring-green-500"
                inputMode="numeric"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={handleConfirm}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold text-sm hover:bg-green-700">
              <Check size={18} /> Confirmar
            </button>
            <button onClick={handleSkip}
              className="px-4 py-3 bg-slate-700 text-content-subtle rounded-xl text-sm hover:bg-slate-600">
              <X size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Scanned items strip */}
      {scannedItems.length > 0 && !lastScanned && (
        <div className="bg-slate-800 px-4 py-3 border-t border-slate-700">
          <p className="text-xs text-content-subtle mb-2">Escaneados ({scannedItems.length})</p>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {scannedItems.map(item => (
              <div key={item.product_id}
                className="flex-shrink-0 px-3 py-2 bg-slate-700 rounded-lg text-xs">
                <p className="font-medium text-white">{item.product_sku}</p>
                <p className="text-content-subtle">Contado: {item.counted_qty}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
